/**
 * Yoink Adventures V2 — app entry.
 *
 * Wires config, store, and router, and mounts the view for the current
 * URL. Each view module exports mount(el, ctx) → cleanup; the router is
 * the only thing that decides what's on screen.
 */
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';

import { createStore } from './core/store.js';
import { createRouter } from './core/router.js';
import { initAuth } from './core/auth.js';
import * as authButton from './ui/auth-button.js';
import * as discover from './features/discover/discover.js';
import * as place from './features/place/place.js';
import * as profile from './features/profile/profile.js';
import * as yoink from './features/yoink/yoink.js';
import * as log from './features/log/log.js';
import * as category from './features/category/category.js';
import * as stamp from './features/category/stamp.js';
import * as browse from './features/category/browse.js';
import * as account from './features/account/account.js';
import * as geography from './features/profile/geography.js';
import * as showcase from './features/profile/showcase.js';
import * as medalTable from './features/profile/medal-table.js';
import * as listPage from './features/lists/list-page.js';
import * as myLists from './features/lists/my-lists.js';
import * as listsImport from './features/lists/import-page.js';
import * as importHub from './features/import/import-hub.js';
import * as importReviews from './features/import/reviews-page.js';
import * as savePage from './features/lists/save-page.js';
import * as featuredLists from './features/lists/featured-page.js';
import * as phonePage from './features/phone/phone-page.js';
import * as routePage from './features/route/route-page.js';
import * as glossary from './features/community/glossary.js';
import * as personalCategory from './features/journal/personal-category-page.js';
import * as admin from './features/admin/admin.js';
import * as nav from './ui/nav.js';
import * as alerts from './ui/alerts.js';
import * as nearSaved from './ui/near-saved.js';
import * as visitorRibbon from './ui/visitor-ribbon.js';
import * as footer from './ui/footer.js';
import { loadFollowing } from './features/follows/follows.js';
import { initUnread } from './features/follows/unread.js';
import { loadAppSettings } from './core/settings.js';
import * as banner from './ui/banner.js';
import * as signupSourcePrompt from './ui/signup-source-prompt.js';
import { initAnalytics, trackRoute } from './core/analytics.js';
import { captureInstallSignals } from './core/device.js';
import { initSrNav } from './ui/sr-nav.js';
import { mergeServerProgress, markComplete, markRouteComplete, completedCount, checklistHtml } from './features/community/tutorial.js';
import { isEmbed } from './ui/embed.js';
import { clearServerContent } from './core/ssr.js';
import { escapeHtml } from '../assets/js/utils.js';

const VIEW_TITLES = {
  discover: 'Discover',
  log: 'Log a yoink',
  'yoink-edit': 'Edit yoink',
  glossary: 'What’s what',
  'personal-category': 'Personal category',
  account: 'My account',
  'user-geography': 'Geography',
  'user-trophies': 'Trophy case',
  'user-awards': 'Awards',
  'user-collections': 'Collections',
  'user-categories': 'Categories',
  'user-lists': 'Lists',
  category: 'Category',
  stamp: 'Mark your visits',
  categories: 'Categories',
  medals: 'Medal table',
  location: 'Location',
  user: 'Explorer',
  yoink: 'Yoink',
  list: 'List',
  lists: 'My lists',
  'lists-import': 'Import from Google Maps',
  'featured-lists': 'Featured lists',
  import: 'Bring your places to Yoink',
  'import-reviews': 'Import your Google reviews',
  save: 'Save a place',
  phone: 'Put Yoink on your phone',
  route: 'On the way',
  admin: 'Admin',
  'not-found': 'Page not found',
};

/** Unknown URLs get a real not-found page, not a silent Discover render. */
const notFoundView = {
  mount(el) {
    el.innerHTML = `
      <div class="v2-placeholder">
        <h2>Page not found</h2>
        <p>That URL doesn’t match anything in the field journal.</p>
        <a href="${window.YOINK_CONFIG?.basePath || '/adventure'}/" data-route="discover">← Back to Discover</a>
      </div>`;
    return () => {};
  },
};

/** Views not yet rebuilt render a friendly placeholder (see docs/V2-PLAN.md phases). */
function placeholderView(routeName) {
  return {
    mount(el) {
      el.innerHTML = `
        <div class="v2-placeholder">
          <h2>${VIEW_TITLES[routeName] || 'This page'}</h2>
          <p>Not rebuilt yet — coming in a later phase.</p>
          <a href="${window.YOINK_CONFIG?.basePath || '/adventure'}/" data-route="discover">← Back to Discover</a>
        </div>`;
      return () => {};
    },
  };
}

const VIEWS = {
  discover,
  location: place,
  user: profile,
  yoink,
  log,
  category,
  stamp,
  categories: browse,
  medals: medalTable,
  account,
  'user-geography': geography,
  'user-trophies': showcase.trophies,
  'user-awards': showcase.userAwards,
  'user-collections': showcase.collections,
  'user-categories': showcase.userCategories,
  'user-lists': showcase.userLists,
  'yoink-edit': log,
  list: listPage,
  lists: myLists,
  'lists-import': listsImport,
  'featured-lists': featuredLists,
  import: importHub,
  'import-reviews': importReviews,
  save: savePage,
  phone: phonePage,
  route: routePage,
  glossary,
  'personal-category': personalCategory,
  admin,
  'not-found': notFoundView,
};

/**
 * V1's UI-freeze guard (app.js:39-55): a stray unhandled rejection or
 * uncaught error must never wedge the view. Swallow it (preventDefault)
 * and log — Sentry wiring is an M9 follow-up (V1-PARITY M9 step 2).
 */
function installErrorGuards() {
  window.addEventListener('unhandledrejection', (event) => {
    event.preventDefault();
    console.warn('[V2] Unhandled rejection:', event.reason?.message || event.reason);
  });
  window.addEventListener('error', (event) => {
    console.warn('[V2] Uncaught error:', event.message);
  });
}

function start() {
  installErrorGuards();
  // Chrome's install prompt fires once, early — capture it before any
  // view mounts so the /phone page can offer a real Install button
  captureInstallSignals();
  const config = window.YOINK_CONFIG;
  const root = document.getElementById('v2-app');
  if (!config?.supabase?.url || !config?.supabase?.anonKey || !root) {
    if (root) root.innerHTML = '<div class="v2-error">App configuration missing.</div>';
    return;
  }

  // Embed mode (?embed=1, the iframe the Embed-map button hands out):
  // this page is a guest on someone else's site. The root class drives
  // the chrome-stripping CSS; nav/banner/alerts never mount; internal
  // navigation opens the full journal in a new tab (below).
  const embedded = isEmbed();
  if (embedded) document.documentElement.classList.add('v2-embed');

  const store = createStore({
    categoryFilter: null,
    searchQuery: '',
    // null = the reader has not picked a side yet, so Discover chooses for
    // them (features/discover: Following once they follow anything). It
    // becomes 'all' or 'following' the moment they tap either button, and
    // stays their choice for the session.
    feedScope: null,
    // Discover's geographic scope: city box from GPS, or the map's last
    // viewport — the grid and the map share it (V1 parity, M8.5)
    feedBounds: null,
    feedBoundsAsked: false,
    // Auth slice — owned by core/auth.js; views subscribe, never poll
    authReady: false,
    user: null,
    profile: null,
    // true while the profile read for the signed-in user is in flight, so
    // the header can hold the name back instead of painting the email handle
    profileLoading: false,
    passwordRecovery: false,
    // Follows slices — owned by features/follows (categories are the
    // primary follow object; user follows are the additive extra)
    followedCategoryIds: [],
    followingIds: [],
    // Unread-follows badge (features/follows/unread.js): null until the
    // first check lands, then the unread rows ([] = caught up)
    unreadFollows: null,
    unreadUnavailable: false,
    // Lists slices — owned by features/lists, loaded lazily by views
    // that show a save button
    wantToGoListId: null,
    savedLocationIds: null,
    listsUnavailable: false,
    // Community slices (core/settings.js)
    isReadOnly: false,
    bannerMessage: null,
  });
  let cleanup = null;

  // Follows ride the auth state: (re)load on login, clear on logout
  let followsUserId = null;
  store.subscribe((state) => {
    const userId = state.user?.id || null;
    if (userId === followsUserId) return;
    followsUserId = userId;
    // Saved-places slices reset on any account change; the next page
    // with a save button lazily reloads them (features/lists)
    store.set({ wantToGoListId: null, savedLocationIds: null, listsUnavailable: false });
    if (userId) {
      loadFollowing(store);
    } else if (state.followingIds.length || state.followedCategoryIds.length) {
      store.set({ followingIds: [], followedCategoryIds: [] });
    }
  });

  // Header auth controls live outside the routed view and react to the
  // auth slice. Auth init is fire-and-forget: browsing never blocks on it.
  const authEl = document.getElementById('v2-auth');
  if (authEl) authButton.mount(authEl, { store });
  initAuth({ store });
  loadAppSettings(store);
  initAnalytics(store);
  initSrNav();

  const bannerEl = document.getElementById('v2-banner');
  if (bannerEl && !embedded) banner.mount(bannerEl, { store });

  // "How did you hear about us?" for accounts that never saw the
  // Create-account form (Google sign-ups). Under the banner, above the
  // app — outside #v2-app so a view repaint can't wipe it
  // (ui/signup-source-prompt.js). Mount point created here so both shells
  // pick it up on the same deploy as the bundle.
  const appRoot = document.getElementById('v2-app');
  if (appRoot && !embedded) {
    const promptEl = document.createElement('div');
    promptEl.id = 'v2-signup-source';
    appRoot.insertAdjacentElement('beforebegin', promptEl);
    signupSourcePrompt.mount(promptEl, { store });
  }

  // Tutorial: server progress merges in when the profile loads; signing
  // in completes the register step (V1 behavior). Merge ONCE per loaded
  // profile object — this subscriber fires on every store change, and
  // re-merging the same in-memory snapshot re-wipes progress earned since
  // an admin reset (the snapshot stays empty until the next profile fetch).
  let tutorialProgressSeen = null;
  store.subscribe((state) => {
    const serverProgress = state.profile?.tutorial_progress;
    if (serverProgress && serverProgress !== tutorialProgressSeen) {
      tutorialProgressSeen = serverProgress;
      mergeServerProgress(serverProgress);
    }
    // Signed in ⇒ the register step is complete, including right after a
    // reset wipes it (markComplete no-ops when already set)
    if (state.user?.id) markComplete('register');
  });

  // ?tutdiag=1 — on-page tutorial diagnostics (primary test device is an
  // iPad with no DevTools; same pattern as ?authdiag=1)
  if (new URLSearchParams(window.location.search).has('tutdiag')) {
    const panel = document.createElement('details');
    panel.open = true;
    panel.style.cssText = 'position:fixed;bottom:8px;left:8px;right:8px;z-index:9999;'
      + 'background:#1b1b16;color:#e8e2cf;font:11px/1.5 monospace;padding:8px;'
      + 'border-radius:8px;max-height:45vh;overflow:auto;opacity:.95';
    document.body.appendChild(panel);
    const refresh = () => {
      const src = document.querySelector('script[src*="app.min.js"]')?.src || '';
      const v = /[?&]v=(\d+)/.exec(src)?.[1];
      let local;
      try { local = localStorage.getItem('yoink_tutorial_progress'); } catch { local = '(unreadable)'; }
      const s = store.get();
      panel.innerHTML = `<summary>tutorial diagnostics</summary><pre style="white-space:pre-wrap;margin:4px 0">${escapeHtml([
        `bundle built: ${v ? new Date(v * 1000).toLocaleString() : '(no ?v= — module scripts?)'}`,
        `user: ${s.user?.id || '(signed out)'}`,
        `server tutorial_progress: ${s.profile ? JSON.stringify(s.profile.tutorial_progress) : '(profile not loaded yet)'}`,
        `local yoink_tutorial_progress: ${local}`,
        `completed: ${completedCount()}/12`,
        `checklist card: ${checklistHtml(!!s.user) ? 'RENDERS' : 'EMPTY (hidden)'}`,
      ].join('\n'))}</pre>`;
    };
    refresh();
    store.subscribe(refresh);
    setInterval(refresh, 2000);
  }

  let firstRoute = true;
  const router = createRouter({
    basePath: config.basePath || '/adventure',
    onChange(route) {
      if (cleanup) cleanup();
      // The crawler-facing block PHP rendered into #v2-app (core/ssr.js).
      // Most views overwrite innerHTML anyway; this is the guarantee that
      // does not rely on them all remembering to.
      clearServerContent(root);
      window.scrollTo(0, 0); // V1 app.js:1255 — every view change starts at the top
      const view = VIEWS[route.name] || placeholderView(route.name);
      document.title = `${VIEW_TITLES[route.name] || 'Yoink'} | Yoink Adventures v2`;
      // Before the view mounts, so a view's sharper setContext line
      // (set after its data loads) is never wiped by this reset
      visitorRibbon.setRoute(route.name);
      trackRoute(route.name);
      markRouteComplete(route.name, { initial: firstRoute });
      firstRoute = false;
      cleanup = view.mount(root, { store, router, params: route.params });
    },
  });

  // In an embed, every internal hop opens the FULL site in a new tab:
  // readers should never be trapped browsing the whole journal inside a
  // 480px window on someone's blog — and each tap-through is the door
  // from the embedding site back to us.
  if (embedded) {
    router.navigate = (name, params = {}) => {
      window.open(window.location.origin + router.generate(name, params), '_blank', 'noopener');
    };
  }

  // A password-reset email link fires PASSWORD_RECOVERY — take them
  // straight to the account page's new-password form.
  let recoveryHandled = false;
  store.subscribe((state) => {
    if (state.passwordRecovery && !recoveryHandled) {
      recoveryHandled = true;
      router.navigate('account');
    }
    if (!state.passwordRecovery) recoveryHandled = false;
  });

  // App chrome: the trifecta nav (Discover | Log a yoink | My journal)
  const navEl = document.getElementById('v2-nav');
  if (navEl && !embedded) nav.mount(navEl, { store, router });

  // Embeds carry their own byline instead: a pinned attribution bar
  // that opens this page on the full site.
  if (embedded) {
    const attribution = document.createElement('a');
    attribution.className = 'v2-embed-attribution';
    // Same page on the full site, minus the embed flag. Everything else
    // in the query string is the page's IDENTITY (a journal drilled into
    // one category is ?category=trail), so dropping it would land the
    // reader on the cover instead of the map they just tapped.
    const fullSiteQuery = new URLSearchParams(window.location.search);
    fullSiteQuery.delete('embed');
    attribution.href = window.location.origin + window.location.pathname
      + (String(fullSiteQuery) ? `?${fullSiteQuery}` : '');
    attribution.target = '_blank';
    attribution.rel = 'noopener';
    attribution.innerHTML = 'Explore this map on <strong>Yoink Adventures</strong> ↗';
    document.body.appendChild(attribution);
  }

  // App footer: the Tell Us box (ui/footer.js). Created here rather than
  // in the shells so both shells pick it up on the same deploy as the
  // bundle, and appended BEFORE the ribbon so the ribbon (fixed) is last
  // in the DOM. Embeds get their attribution bar instead — a 480px window
  // on someone's blog is not the place to write us a note.
  if (!embedded) {
    const footerEl = document.createElement('div');
    footerEl.id = 'v2-footer';
    document.body.appendChild(footerEl);
    footer.mount(footerEl, { store, router });
  }

  // Visitor ribbon: the signed-out "what is this and why join" line,
  // fixed to the bottom of shared-link arrivals (ui/visitor-ribbon.js).
  // Mount point created here so both shells pick it up on the same
  // deploy as the bundle; embeds carry their attribution bar instead.
  if (!embedded) {
    const ribbonEl = document.createElement('div');
    ribbonEl.id = 'v2-visitor-ribbon';
    document.body.appendChild(ribbonEl);
    visitorRibbon.mount(ribbonEl, { store });
  }

  // Header bell: unread yoinks from followed explorers. Its mount point
  // is created here rather than in the shells so both shells (subdomain
  // index.php and the flipped yoink-page.php) pick it up on the same
  // deploy as the bundle.
  const headerEl = document.querySelector('.v2-header');
  const authAnchor = document.getElementById('v2-auth');
  if (headerEl && authAnchor && !embedded) {
    const alertsEl = document.createElement('div');
    alertsEl.id = 'v2-alerts';
    alertsEl.className = 'v2-alerts';
    headerEl.insertBefore(alertsEl, authAnchor);
    alerts.mount(alertsEl, { store, router });
    // Proximity light: lit while a Want-to-go place is within range of a
    // real GPS fix. Left of the bell, same hidden-until-relevant deal.
    const nearEl = document.createElement('div');
    nearEl.id = 'v2-near-saved';
    nearEl.className = 'v2-alerts v2-near';
    headerEl.insertBefore(nearEl, alertsEl);
    nearSaved.mount(nearEl, { store, router });
  }
  initUnread(store);

  // Intercept internal links so placeholder back-links use the router
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-route]');
    if (!link) return;
    event.preventDefault();
    router.navigate(link.dataset.route);
  });

  router.init();
}

start();
